/**
 * BreakdownWidget — wykres kołowy (donut): rozkład metryki po firmach użytkownika albo po
 * statusach dokumentów (wzór Figmy „Typy kont": suma w środku, legenda z udziałem %).
 *
 * Wymiar `company` jest niezależny od aktywnej firmy (to rozkład po WSZYSTKICH firmach usera);
 * wymiar `status` liczy aktywną firmę albo — w trybie „Wszystkie firmy" — sumę po firmach.
 * Uprawnienia sprawdza backend osobno dla każdej firmy. Kolory: tokeny `chart/1–6` z Figmy
 * (jasny/ciemny wariant przez `useChartTheme`).
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useChartTheme } from '@/lib/use-chart-theme';

import { classifyDataError, fetchBreakdown } from '../dashboard-api';
import type { BreakdownDimension, WidgetRenderProps } from '../types';

import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

const TOOLTIP_STYLE = {
  fontSize: 12,
  borderRadius: 8,
  border: '1px solid rgb(var(--border-secondary))',
  background: 'rgb(var(--bg-elevated))',
  color: 'rgb(var(--text-primary))',
};

export function BreakdownWidget({ widget, period = 'all' }: WidgetRenderProps) {
  const { t } = useTranslation();
  const theme = useChartTheme();
  const { isAll, mode, companyId } = useActiveCompanyScope();

  const cfg = widget.type === 'breakdown' ? widget.config : null;
  const metric = cfg?.metric ?? null;
  const dimension: BreakdownDimension = cfg?.dimension ?? 'company';
  // Rozkład po statusach jest per firma — bez żadnej firmy nie ma czego liczyć.
  const blocked = dimension === 'status' && mode === 'none';

  const query = useQuery({
    queryKey: [
      'tenant',
      'dashboard',
      'breakdown',
      metric,
      period,
      dimension,
      dimension === 'status' ? (companyId ?? 'all') : 'tenant',
    ],
    queryFn: () => fetchBreakdown(metric!, period, dimension, isAll ? 'all' : 'company'),
    enabled: metric !== null && !blocked,
    staleTime: 60_000,
    retry: false,
  });

  if (widget.type !== 'breakdown') return null;

  if (blocked) return <WidgetEmpty message={t('tenant.dashboard.pickCompany')} />;
  if (query.isLoading) return <WidgetLoading />;
  if (query.isError)
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );

  const raw = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  if (raw.length === 0 || total === 0) {
    return <WidgetEmpty message={t('tenant.dashboard.widgets.breakdown.empty')} />;
  }

  // Nazwy statusów tłumaczy front (backend zwraca kody); firmy mają gotowe nazwy.
  const items = raw.map((item) => ({
    ...item,
    id: item.key ?? item.companyId ?? item.name,
    label:
      dimension === 'status'
        ? t(`tenant.dashboard.status.${item.key ?? item.name}`, { defaultValue: item.name })
        : item.name,
  }));
  const colorOf = (i: number) => theme.palette[i % theme.palette.length]!;

  return (
    <div className="flex h-full min-h-0 flex-col items-center gap-3 sm:flex-row">
      {/* Donut pozycjonowany absolutnie w `relative` rodzicu — patrz komentarz w ChartWidget. */}
      <div className="relative h-full min-h-[130px] w-full flex-1 sm:w-1/2">
        <div className="absolute inset-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={items}
                dataKey="count"
                nameKey="label"
                innerRadius="64%"
                outerRadius="94%"
                paddingAngle={items.length > 1 ? 2 : 0}
                stroke="none"
              >
                {items.map((item, i) => (
                  <Cell key={item.id} fill={colorOf(i)} />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold tabular-nums text-text-primary">
            {total.toLocaleString('pl-PL')}
          </span>
          <span className="text-[11px] text-text-tertiary">
            {t(`tenant.dashboard.metrics.${metric}`)}
          </span>
        </div>
      </div>

      <ul className="w-full min-w-0 space-y-2 text-xs sm:w-1/2">
        {items.map((item, i) => (
          <li key={item.id} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: colorOf(i) }}
            />
            <span className="min-w-0 flex-1 truncate text-text-secondary" title={item.label}>
              {item.label}
            </span>
            <span className="tabular-nums text-text-primary">
              {Math.round((item.count / total) * 100)} %
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
